/**
 * CreateDocumentButton - Unified document creation entry point
 * Work Order Phase 4: Make document creation "obviously discoverable"
 * Work Order 2 Phase 4: RBAC-aware filtering
 * 
 * Dropdown menu with 5 core actions (RBAC-filtered):
 * - Record Event (→ journal) - canWriteJournal
 * - Upload PDF (→ exhibits/upload) - canWriteExhibits
 * - Fill a Form (→ forms) - canWriteForms
 * - Draft Document (→ drafts) - canWriteForms
 * - Create Filing (→ filings) - canWriteFilings
 */

import React, { Fragment, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Menu, Transition } from '@headlessui/react';
import { Plus, ChevronDown, Upload, FileText, PenTool, Gavel, BookOpen } from 'lucide-react';
import { useCasePermissions } from '@/context/caseAccess/CaseAccessContext';

interface CreateDocumentButtonProps {
  caseId: string;
  variant?: 'primary' | 'secondary';
  size?: 'sm' | 'md' | 'lg';
}

export const CreateDocumentButton: React.FC<CreateDocumentButtonProps> = ({ 
  caseId,
  variant = 'primary',
  size = 'md',
}) => {
  const navigate = useNavigate();
  const { permissions, loading } = useCasePermissions();

  // RBAC-aware actions (WO2 Phase 4)
  const allActions = [
    {
      id: 'record-event',
      label: 'Record an Event',
      description: 'Document what happened',
      icon: BookOpen,
      onClick: () => navigate(`/cases/${caseId}/journal`),
      requiresPermission: 'canWriteJournal' as const,
    },
    {
      id: 'upload-pdf',
      label: 'Upload Documents',
      description: 'Add evidence or files',
      icon: Upload,
      onClick: () => navigate(`/cases/${caseId}/exhibits`),
      requiresPermission: 'canWriteExhibits' as const,
    },
    {
      id: 'fill-form',
      label: 'Fill a Form',
      description: 'Complete court documents',
      icon: FileText,
      onClick: () => navigate(`/cases/${caseId}/forms`),
      requiresPermission: 'canWriteForms' as const,
    },
    {
      id: 'draft-document',
      label: 'Draft a Document',
      description: 'Write declarations or letters',
      icon: PenTool,
      onClick: () => navigate(`/cases/${caseId}/drafts`),
      requiresPermission: 'canWriteForms' as const,
    },
    {
      id: 'create-filing',
      label: 'Create a Filing',
      description: 'Prepare court submission',
      icon: Gavel,
      onClick: () => navigate(`/cases/${caseId}/filings`),
      requiresPermission: 'canWriteFilings' as const,
    },
  ];

  // Filter actions based on permissions
  const actions = allActions.filter(action => {
    if (!permissions) return false; // Loading state or no permissions
    return permissions[action.requiresPermission] === true;
  });

  // Hide button entirely if user has no write permissions
  if (loading || actions.length === 0) {
    return null;
  }

  // Variant styles
  const baseClasses = variant === 'primary'
    ? 'bg-brand-orange hover:bg-brand-orange/90 text-white'
    : 'bg-slate-700 hover:bg-slate-600 text-slate-200 border border-slate-600';

  // Size styles
  const sizeClasses = {
    sm: 'px-3 py-1.5 text-sm',
    md: 'px-4 py-2 text-sm',
    lg: 'px-5 py-3 text-base',
  };

  const iconSize = {
    sm: 16,
    md: 18,
    lg: 20,
  };

  return (
    <Menu as="div" className="relative">
      {({ open }) => (
        <>
          <Menu.Button
            className={`
              inline-flex items-center gap-2 rounded-lg font-medium transition-colors
              focus:outline-none focus:ring-2 focus:ring-brand-orange focus:ring-offset-2 focus:ring-offset-slate-900
              ${baseClasses} ${sizeClasses[size]}
            `}
          >
            <Plus size={iconSize[size]} />
            <span>Create Document</span>
            <ChevronDown 
              size={iconSize[size] - 2} 
              className={`transition-transform ${open ? 'rotate-180' : ''}`} 
            />
          </Menu.Button>

          <Transition
            as={Fragment}
            enter="transition ease-out duration-100"
            enterFrom="transform opacity-0 scale-95"
            enterTo="transform opacity-100 scale-100"
            leave="transition ease-in duration-75"
            leaveFrom="transform opacity-100 scale-100"
            leaveTo="transform opacity-0 scale-95"
          >
            <Menu.Items className="absolute right-0 mt-2 w-72 origin-top-right rounded-lg border border-slate-700 bg-slate-800 shadow-xl focus:outline-none z-50">
              <div className="p-1">
                {actions.map((action) => {
                  const Icon = action.icon;
                  return (
                    <Menu.Item key={action.id}>
                      {({ active }) => (
                        <button
                          onClick={action.onClick}
                          className={`
                            w-full flex items-start gap-3 rounded-md px-3 py-2.5 text-left transition
                            ${active ? 'bg-slate-700 text-white' : 'text-slate-300'}
                          `}
                        >
                          <div className={`
                            flex-shrink-0 rounded-md p-2 
                            ${active ? 'bg-brand-orange/20 text-brand-orange' : 'bg-slate-700 text-slate-400'}
                          `}>
                            <Icon size={18} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="font-medium text-sm">{action.label}</div>
                            <div className="text-xs text-slate-400 mt-0.5">{action.description}</div>
                          </div>
                        </button>
                      )}
                    </Menu.Item>
                  );
                })}
              </div>

              {/* Tip */}
              <div className="border-t border-slate-700 px-3 py-2 bg-slate-900/50">
                <p className="text-xs text-slate-400">
                  💡 All your documents appear in one place:{' '}
                  <button
                    onClick={() => navigate(`/cases/${caseId}/documents`)}
                    className="text-brand-orange hover:underline font-medium"
                  >
                    Documents Center
                  </button>
                </p>
              </div>
            </Menu.Items>
          </Transition>
        </>
      )}
    </Menu>
  );
};
